"use client";

import { IconCheck, IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import CircleFilledSmall from "@/assets/icons/circle-filled-small.svg";
import { PASSWORD_RULES } from "@/features/auth/lib/password-rules";

type RuleState = "pending" | "met" | "failed";

const STATE_ICONS = {
  met: IconCheck,
  failed: IconX,
} as const satisfies Record<Exclude<RuleState, "pending">, unknown>;

const STATE_ICON_CLASSES = {
  pending: "text-icon-subtle",
  met: "text-icon-success",
  failed: "text-icon-danger",
} as const satisfies Record<RuleState, string>;

const getRuleState = (value: string, isMet: boolean): RuleState => {
  if (!value) {
    return "pending";
  }

  return isMet ? "met" : "failed";
};

/**
 * Live checklist under the password field.
 *
 * Figma shows it from the moment the field is in play — focused or filled — and
 * hides it on an untouched form. While the field is still empty the rules stay
 * neutral; once the user types, the icon carries the verdict: green check for a
 * met rule, red cross for a broken one. Labels keep the text colour so the list
 * reads as one block.
 */
export const PasswordRequirements = ({
  value,
  isFocused,
  alwaysVisible = false,
}: {
  value: string;
  isFocused: boolean;
  alwaysVisible?: boolean;
}) => {
  const t = useTranslations("auth.createPassword.requirements");

  if (!(alwaysVisible || value || isFocused)) {
    return null;
  }

  return (
    <ul className="flex flex-col gap-1">
      {PASSWORD_RULES.map((rule) => {
        const state = getRuleState(value, rule.test(value));
        const Icon = state === "pending" ? null : STATE_ICONS[state];
        const iconClassName = `size-6 shrink-0 ${STATE_ICON_CLASSES[state]}`;

        return (
          <li className="flex items-center gap-2" key={rule.id}>
            {Icon ? (
              <Icon aria-hidden="true" className={iconClassName} stroke={1.5} />
            ) : (
              <CircleFilledSmall aria-hidden="true" className={iconClassName} />
            )}
            <span className="text-sm text-text leading-[22px]">
              {t(rule.id)}
            </span>
          </li>
        );
      })}
    </ul>
  );
};
